import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { Bell, BellOff, Check, Clock, Shield, TrendingUp, Search, Eye, GitCompare, BookOpen, AlertTriangle } from "lucide-react";

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

const NOTIF_TYPE_META = {
  weekly_report: { icon: Clock, color: "text-blue-600", bg: "bg-blue-50" },
  scan_completed: { icon: Search, color: "text-emerald-600", bg: "bg-emerald-50" },
  rank_change: { icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
  brand_monitor: { icon: Eye, color: "text-purple-600", bg: "bg-purple-50" },
  competitor_alert: { icon: GitCompare, color: "text-red-600", bg: "bg-red-50" },
  blog_agent: { icon: BookOpen, color: "text-rose-600", bg: "bg-rose-50" },
  security_alert: { icon: AlertTriangle, color: "text-orange-600", bg: "bg-orange-50" },
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export default function NotificationsTab() {
  const { t } = useLanguage();
  const [prefs, setPrefs] = useState({});
  const [subscribed, setSubscribed] = useState(false);
  const [types, setTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [permState, setPermState] = useState("default");

  const fetchData = useCallback(async () => {
    try {
      const [prefsRes, histRes] = await Promise.all([
        axios.get(`${API}/user-notifications/preferences`, { withCredentials: true }),
        axios.get(`${API}/user-notifications/history`, { withCredentials: true }),
      ]);
      setPrefs(prefsRes.data.preferences);
      setSubscribed(prefsRes.data.subscribed);
      setTypes(prefsRes.data.types);
      setHistory(histRes.data.notifications);
      setUnread(histRes.data.unread);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
    if ("Notification" in window) setPermState(Notification.permission);
  }, [fetchData]);

  const handleSubscribe = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      alert(t.notif_not_supported || "Push notifications not supported in this browser");
      return;
    }

    const permission = await Notification.requestPermission();
    setPermState(permission);
    if (permission !== "granted") return;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
    });

    const subJson = sub.toJSON();
    await axios.post(`${API}/user-notifications/subscribe`, {
      endpoint: subJson.endpoint,
      keys: subJson.keys,
    }, { withCredentials: true });

    setSubscribed(true);
  };

  const handleUnsubscribe = async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
    await axios.delete(`${API}/user-notifications/unsubscribe`, { withCredentials: true });
    setSubscribed(false);
  };

  const togglePref = async (type) => {
    const updated = { ...prefs, [type]: !prefs[type] };
    setPrefs(updated);
    await axios.put(`${API}/user-notifications/preferences`, { preferences: updated }, { withCredentials: true });
  };

  const markAllRead = async () => {
    await axios.put(`${API}/user-notifications/read-all`, {}, { withCredentials: true });
    setHistory(h => h.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  return (
    <div className="max-w-3xl space-y-8" data-testid="notifications-tab">
      <div>
        <h2 className="text-2xl font-black font-['Chivo'] text-gray-900 flex items-center gap-2">
          <Bell className="w-6 h-6 text-blue-600" />
          {t.notif_title || "Notifications"}
        </h2>
        <p className="text-gray-500 mt-1">{t.notif_subtitle || "Get notified about important SEO changes, scans and reports"}</p>
      </div>

      {/* Push subscription toggle */}
      <div className="bg-white border border-gray-200 p-5" data-testid="push-subscription-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">{t.notif_push_title || "Push Notifications"}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {subscribed
                ? (t.notif_push_on || "You're receiving push notifications on this device")
                : (t.notif_push_off || "Enable to receive notifications even when the app is closed")}
            </p>
          </div>
          {subscribed ? (
            <button onClick={handleUnsubscribe} className="px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50" data-testid="push-unsubscribe-btn">
              <BellOff className="w-4 h-4 inline mr-1" /> {t.notif_disable || "Disable"}
            </button>
          ) : (
            <button onClick={handleSubscribe} className="px-4 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-700" data-testid="push-subscribe-btn">
              <Bell className="w-4 h-4 inline mr-1" /> {t.notif_enable || "Enable"}
            </button>
          )}
        </div>
        {permState === "denied" && (
          <p className="text-xs text-red-600 mt-2">{t.notif_denied || "Notifications blocked by browser. Please allow in browser settings."}</p>
        )}
      </div>

      {/* Notification type preferences */}
      <div className="bg-white border border-gray-200 p-5 space-y-1" data-testid="notif-preferences">
        <h3 className="font-bold text-gray-900 mb-3">{t.notif_types_title || "Notification Types"}</h3>
        {types.map((type) => {
          const meta = NOTIF_TYPE_META[type] || { icon: Bell, color: "text-gray-600", bg: "bg-gray-50" };
          const Icon = meta.icon;
          const enabled = prefs[type] !== false;
          return (
            <button
              key={type}
              onClick={() => togglePref(type)}
              className="w-full flex items-center gap-3 px-3 py-3 hover:bg-gray-50 transition-colors text-left"
              data-testid={`notif-toggle-${type}`}
            >
              <div className={`w-8 h-8 ${meta.bg} flex items-center justify-center shrink-0`}>
                <Icon className={`w-4 h-4 ${meta.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t[`notif_${type}`] || type.replace(/_/g, ' ')}</p>
                <p className="text-xs text-gray-400">{t[`notif_${type}_desc`] || ""}</p>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-200'} relative`}>
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
              </div>
            </button>
          );
        })}
      </div>

      {/* Notification history */}
      <div className="bg-white border border-gray-200 p-5" data-testid="notif-history">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-gray-900">
            {t.notif_history || "Recent Notifications"}
            {unread > 0 && <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 font-bold">{unread}</span>}
          </h3>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline" data-testid="notif-read-all">
              {t.notif_mark_read || "Mark all read"}
            </button>
          )}
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">{t.notif_empty || "No notifications yet"}</p>
        ) : (
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {history.map((n, i) => {
              const meta = NOTIF_TYPE_META[n.type] || { icon: Bell, color: "text-gray-600", bg: "bg-gray-50" };
              const Icon = meta.icon;
              return (
                <div key={i} className={`flex items-start gap-3 px-3 py-2.5 ${n.read ? '' : 'bg-blue-50/50'}`} data-testid={`notif-item-${i}`}>
                  <div className={`w-7 h-7 ${meta.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <Icon className={`w-3.5 h-3.5 ${meta.color}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{n.title}</p>
                    <p className="text-xs text-gray-500">{n.body}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-blue-600 rounded-full shrink-0 mt-2" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
